/**
 * Influence Score + Badges (Macro #23, private owner surface).
 *
 * Shows the EXACT lifetime Influence Points (private), the current badge,
 * progress to the next threshold and the by-role breakdown. Public surfaces
 * elsewhere expose ONLY the compact badge — never the exact score.
 */
import type { InfluenceSummary } from "../../lib/rewards-api";
import { Bar, BADGE_LADDER, BadgePill, Card } from "./primitives";
import { useI18n } from "../../i18n";

const ROLE_LABEL: Record<string, string> = {
  BUYER: "Buyer",
  REFERRER: "Referrer",
  NEIGHBOR: "Neighbor",
};

export function InfluenceCard({ influence }: { influence: InfluenceSummary }) {
  const { t } = useI18n();
  const pts = influence.influence_points;
  return (
    <Card
      title={t("profile.influence.title")}
      testId="influence-card"
      right={<BadgePill badge={influence.badge} testId="influence-badge" />}
    >
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wide text-white/45">{t("profile.influence.lifetimePrivate")}</div>
          <div className="text-3xl font-semibold tabular-nums text-white" data-testid="influence-points">{pts.toLocaleString()}</div>
        </div>
        <div className="text-right text-[11px] text-white/45">
          {influence.next_badge ? (
            <span data-testid="influence-next">
              {t("profile.influence.ptsTo", { points: influence.points_to_next.toLocaleString() })}<span className="text-amber-200/90">{influence.next_badge}</span>
            </span>
          ) : (
            <span data-testid="influence-max">{t("profile.influence.maxReached")}</span>
          )}
        </div>
      </div>

      <div className="mt-3">
        <Bar value={influence.progress_pct} tint="bg-amber-400/70" />
        <div className="mt-1 flex justify-between font-mono text-[10px] text-white/40">
          <span data-testid="influence-progress-pct">{influence.progress_pct}%</span>
          {influence.next_badge_threshold != null && (
            <span>{t("profile.influence.nextAt", { threshold: influence.next_badge_threshold.toLocaleString() })}</span>
          )}
        </div>
      </div>

      {/* Badge ladder */}
      <div className="mt-4 grid grid-cols-5 gap-1.5" data-testid="influence-badge-ladder">
        {BADGE_LADDER.map(([threshold, name]) => {
          const reached = pts >= threshold;
          return (
            <div
              key={name}
              data-testid={`badge-rung-${name}`}
              data-reached={reached}
              className={`rounded-lg border px-1.5 py-2 text-center ${reached ? "border-amber-300/40 bg-amber-400/10" : "border-white/10 bg-white/[0.02]"}`}
            >
              <div className={`font-mono text-[8.5px] uppercase tracking-wide ${reached ? "text-amber-200" : "text-white/35"}`}>{name}</div>
              <div className={`mt-0.5 text-[10px] tabular-nums ${reached ? "text-amber-100/80" : "text-white/30"}`}>{threshold >= 1000 ? `${threshold / 1000}k` : threshold}</div>
            </div>
          );
        })}
      </div>

      {/* By-role breakdown */}
      {Object.keys(influence.by_role).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="influence-by-role">
          {Object.entries(influence.by_role).map(([role, v]) => (
            <span key={role} className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 font-mono text-[10px] uppercase tracking-wide text-white/55">
              {ROLE_LABEL[role] ?? role}: <span className="text-white/80">{v.toLocaleString()}</span>
            </span>
          ))}
        </div>
      )}
      <p className="mt-3 text-[11px] text-white/35">{t("profile.influence.publicNote")}</p>
    </Card>
  );
}
